import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet } from "../../services/api";
import type { Transaction, Location } from "../../types";

export default function DailySummaryTab() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterLocation, setFilterLocation] = useState("");

  useEffect(() => { if (orgId) loadData(); }, [orgId]);

  async function loadData() {
    try {
      const [txns, locs] = await Promise.all([
        apiGet<Transaction[]>(`/organizations/${orgId}/transactions`),
        apiGet<Location[]>(`/organizations/${orgId}/locations`),
      ]);
      setTransactions(txns as any[]);
      setLocations(locs);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  const dailySummary = useMemo(() => {
    const filtered = filterLocation
      ? transactions.filter((t) => t.locationId === filterLocation)
      : transactions;

    const byDay: Record<string, { date: string; count: number; total: number; cash: number; card: number }> = {};

    filtered.forEach((txn: any) => {
      const date = new Date(txn.createdAt).toLocaleDateString();
      if (!byDay[date]) byDay[date] = { date, count: 0, total: 0, cash: 0, card: 0 };
      byDay[date].count++;
      byDay[date].total += txn.total || 0;
      if (txn.paymentMethod === "cash") byDay[date].cash += txn.total || 0;
      else byDay[date].card += txn.total || 0;
    });

    return Object.values(byDay).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [transactions, filterLocation]);

  if (loading) return <div className="empty-state">Loading summary...</div>;

  const grandTotal = dailySummary.reduce((s, d) => s + d.total, 0);
  const totalTxns = dailySummary.reduce((s, d) => s + d.count, 0);

  return (
    <div>
      <div className="org-mgmt-header">
        <h2>Daily Summary</h2>
        <select value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)} style={{ padding: "0.5rem 0.75rem", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "0.85rem" }}>
          <option value="">All Locations</option>
          {locations.map((l: any) => <option key={l.locationId} value={l.locationId}>{l.name}</option>)}
        </select>
      </div>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "1.25rem" }}>
          <div style={{ fontSize: "0.8rem", color: "#6b7280", marginBottom: "0.25rem" }}>Total Revenue</div>
          <div style={{ fontSize: "1.5rem", fontWeight: 700 }}>${grandTotal.toFixed(2)}</div>
        </div>
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "1.25rem" }}>
          <div style={{ fontSize: "0.8rem", color: "#6b7280", marginBottom: "0.25rem" }}>Total Transactions</div>
          <div style={{ fontSize: "1.5rem", fontWeight: 700 }}>{totalTxns}</div>
        </div>
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "1.25rem" }}>
          <div style={{ fontSize: "0.8rem", color: "#6b7280", marginBottom: "0.25rem" }}>Avg per Transaction</div>
          <div style={{ fontSize: "1.5rem", fontWeight: 700 }}>${totalTxns > 0 ? (grandTotal / totalTxns).toFixed(2) : "0.00"}</div>
        </div>
      </div>

      {dailySummary.length === 0 ? (
        <div className="empty-state"><p>No sales data yet.</p></div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Transactions</th>
              <th>Cash</th>
              <th>Card</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {dailySummary.map((day) => (
              <tr key={day.date}>
                <td style={{ fontWeight: 500 }}>{day.date}</td>
                <td>{day.count}</td>
                <td>${day.cash.toFixed(2)}</td>
                <td>${day.card.toFixed(2)}</td>
                <td style={{ fontWeight: 600 }}>${day.total.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
